import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTopClips, getPrematchClips } from '../../services/HighlightsService';
import { HighlightClip } from '../../models/HighlightClip';
import { baseURL } from '../../Constants';
import { playerPhotoUrl } from '../../utils/leagueImages';
import './ClipsOverlay.css';

const formatMapName = (mapName: string | undefined | null): string => {
    if (!mapName) return 'Unknown';
    const cleanName = mapName.replace('de_', '');
    return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
};

const isClutchType = (type: string | undefined): boolean => {
    if (!type) return false;
    return type.toLowerCase().startsWith('clutch');
};

const getClutchLabel = (type: string): string => {
    const lower = type.toLowerCase();
    if (lower === 'clutch1v2') return '1v2';
    if (lower === 'clutch1v3') return '1v3';
    if (lower === 'clutch1v4') return '1v4';
    if (lower === 'clutch1v5') return '1v5';
    if (lower === 'clutch2v4') return '2v4';
    if (lower === 'clutch2v5') return '2v5';
    return 'Clutch';
};

const getHighlightLabel = (clip: HighlightClip): string => {
    if (isClutchType(clip.highlightType)) {
        return getClutchLabel(clip.highlightType);
    }
    if (clip.killCount >= 5) return 'ACE';
    return `${clip.killCount}K`;
};

const ClipsOverlay: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [clips, setClips] = useState<HighlightClip[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [transitioning, setTransitioning] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    const season = searchParams.get('season') ? Number(searchParams.get('season')) : undefined;
    const division = searchParams.get('division') || undefined;
    const limit = searchParams.get('limit') ? Number(searchParams.get('limit')) : 20;
    const sound = searchParams.get('sound') === 'true';
    const matchId = searchParams.get('matchId') || undefined;

    useEffect(() => {
        const fetchClips = async () => {
            try {
                const result = matchId
                    ? await getPrematchClips(matchId, season, limit)
                    : await getTopClips(season, division, undefined, undefined, limit);
                setClips(result);
            } catch (error) {
                console.error('Error fetching clips for overlay:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchClips();
    }, [season, division, limit, matchId]);

    const handleEnded = useCallback(() => {
        // Start fade-out transition
        setTransitioning(true);
        setTimeout(() => {
            setCurrentIndex(prev => (prev + 1) % clips.length);
            // Fade back in after index changes
            setTimeout(() => setTransitioning(false), 100);
        }, 500);
    }, [clips.length]);

    // Auto-play when index changes
    useEffect(() => {
        if (videoRef.current && clips.length > 0) {
            videoRef.current.load();
            videoRef.current.play().catch(() => {});
        }
    }, [currentIndex, clips]);

    if (loading) {
        return <div className="overlay-loading">Loading clips...</div>;
    }

    if (clips.length === 0) {
        return <div className="overlay-loading">No clips available</div>;
    }

    const currentClip = clips[currentIndex];
    const avatarUrl = `${baseURL}/Account/Profile/${currentClip.steamId}?width=96`;
    const fallbackAvatarUrl = playerPhotoUrl(currentClip.steamId, 96);

    return (
        <div className="clips-overlay">
            <div className={`clips-overlay-content ${transitioning ? 'fade-out' : 'fade-in'}`}>
                <video
                    ref={videoRef}
                    className="overlay-video"
                    autoPlay
                    muted={!sound}
                    playsInline
                    src={currentClip.clipUrl}
                    onEnded={handleEnded}
                />

                {/* Bottom-left info overlay */}
                <div className="overlay-info">
                    <div className="overlay-rank">#{currentIndex + 1}</div>
                    <div className="overlay-avatar">
                        <img
                            src={avatarUrl}
                            alt=""
                            onError={(e) => {
                                // Fall back to the CDN once; comparing against the resolved URL
                                // (rather than sniffing for a hostname) stops a failing CDN looping.
                                const target = e.target as HTMLImageElement;
                                if (target.src !== fallbackAvatarUrl) {
                                    target.src = fallbackAvatarUrl;
                                }
                            }}
                        />
                    </div>
                    <div className="overlay-details">
                        <div className="overlay-player-name">{currentClip.playerName || 'Unknown'}</div>
                        <div className="overlay-clip-type">
                            {getHighlightLabel(currentClip)} - {formatMapName(currentClip.mapName)}
                        </div>
                    </div>
                </div>

                {/* Progress indicator */}
                <div className="overlay-progress">
                    {clips.map((_, i) => (
                        <div
                            key={i}
                            className={`overlay-progress-dot ${i === currentIndex ? 'active' : ''} ${i < currentIndex ? 'played' : ''}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ClipsOverlay;
